import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import { appDate } from '../../../../utils/appDateFormat';
import { parseApiDateOnly } from '@/utils/apiDate';

const TemplateDetailsModal = ({ isOpen, onClose, template, onEditTemplate, onDeleteTemplate }) => {
  const { t } = useTranslation();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cascadeDelete, setCascadeDelete] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setConfirmDelete(false);
      setDeleting(false);
      setCascadeDelete(true);
    }
  }, [isOpen, template?.id]);

  if (!isOpen || !template) return null;

  // Get assigned users from template or from the first recurring rule
  const assignedUsers = template?.assignedUsers?.length > 0
    ? template.assignedUsers.map(u => {
        const id = typeof u === 'object' ? (u.id || u.userId) : u;
        const name = (typeof u === 'object') ? (u.fullName || u.name || u.email) : undefined;
        const departmentObj = (typeof u === 'object') ? (u.department || {}) : {};
        const department = (typeof departmentObj === 'string') ? departmentObj : (departmentObj?.name || departmentObj?.title || '');
        return { id, fullName: name, department };
      })
    : (template?.assignedUserIds || []).map(u => {
        const id = u;
        const user = (window.__allUsers || []).find(x => x.id === id);
        // Never fall back to the id: it is the internal user key (owner rule 2026-10-28).
        const name = user?.fullName || user?.name || user?.email || t('people.formerEmployee');
        const department = typeof user?.department === 'string' ? user.department : (user?.department?.name || user?.department?.title || '');
        return { id, fullName: name, department };
      });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: template?.color }}
              ></div>
              <DialogTitle>{template?.name}</DialogTitle>
            </div>
            <button
              type="button"
              onClick={() => {
                onEditTemplate?.(template);
                onClose?.();
              }}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              title={t('EditTemplate')}
            >
              <Icon name="Pencil" size={18} />
            </button>
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-6">
            {/* Delete Confirmation */}
            {confirmDelete && (
              <div className="border border-destructive/30 bg-destructive/10 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-destructive">{t('DeleteTemplate')}</div>
                    <div className="text-sm text-destructive/80 mt-1">
                      {t('DeleteTemplateWarning')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="text-destructive hover:text-destructive/80"
                    disabled={deleting}
                    title={t('Cancel')}
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={cascadeDelete}
                    onChange={(e) => setCascadeDelete(!!e.target.checked)}
                    disabled={deleting}
                  />
                  {t('DeleteAssociatedRulesAndShifts')}
                </label>

                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      try {
                        setDeleting(true);
                        await onDeleteTemplate?.(template, { cascade: cascadeDelete });
                        setConfirmDelete(false);
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={deleting}
                  >
                    {deleting ? t('Deleting') : t('Delete')}
                  </Button>
                </div>
              </div>
            )}

            {/* Template Info */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">{t('TemplateInformation')}</h3>
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('Description')}:</span>
                  <span className="text-sm font-medium text-foreground">{template?.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('Status', { defaultValue: 'Status' })}:</span>
                  <span className="text-sm font-medium text-foreground">
                    {template?.status === 'Inactive'
                      ? t('Inactive', { defaultValue: 'Inactive' })
                      : t('Active', { defaultValue: 'Active' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('Assigned Employees')}:</span>
                  <span className="text-sm font-medium text-foreground">{template?.stats?.totalUsers}</span>
                </div>
              </div>
            </div>

            {/* Recurring Rules */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">{t('RecurringRules')}</h3>
              <div className="space-y-3">
                {/* One card per period, oldest first: a split schedule has several with the same days and times. */}
                {[...(template?.recurringRules || [])]
                  .sort((a, b) => String(a?.startDateUtc || '').localeCompare(String(b?.startDateUtc || '')))
                  .map((rule) => {
                  // Convert minutes to time strings for display
                  const startTime = `${Math.floor(rule.startTimeOfDayMinutes / 60)}:${(rule.startTimeOfDayMinutes % 60).toString().padStart(2, '0')}`;
                  const endTime = `${Math.floor(rule.endTimeOfDayMinutes / 60)}:${(rule.endTimeOfDayMinutes % 60).toString().padStart(2, '0')}`;

                  const fmtMinutes = (m) => `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, '0')}`;
                  const hasBreakInfo =
                    rule?.breakStartTimeOfDayMinutes != null ||
                    rule?.breakEndTimeOfDayMinutes != null ||
                    (rule?.breakDurationMinutes != null && Number(rule.breakDurationMinutes) > 0);

                  const breakStart = typeof rule?.breakStartTimeOfDayMinutes === 'number' ? fmtMinutes(rule.breakStartTimeOfDayMinutes) : null;
                  const breakEnd = typeof rule?.breakEndTimeOfDayMinutes === 'number' ? fmtMinutes(rule.breakEndTimeOfDayMinutes) : null;

                  let inferredDuration = null;
                  if (typeof rule?.breakStartTimeOfDayMinutes === 'number' && typeof rule?.breakEndTimeOfDayMinutes === 'number') {
                    const overnight = rule.endTimeOfDayMinutes <= rule.startTimeOfDayMinutes;
                    let startOff = rule.breakStartTimeOfDayMinutes - rule.startTimeOfDayMinutes;
                    let endOff = rule.breakEndTimeOfDayMinutes - rule.startTimeOfDayMinutes;
                    if (overnight && startOff < 0) startOff += 1440;
                    if (overnight && endOff < 0) endOff += 1440;
                    if (endOff > startOff) inferredDuration = endOff - startOff;
                  }

                  const durationValue = (rule?.breakDurationMinutes != null && Number(rule.breakDurationMinutes) > 0)
                    ? Number(rule.breakDurationMinutes)
                    : inferredDuration;

                  return (
                    <div key={rule?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-foreground">{t('RecurringRule')}</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('Recurrence')}:</span>
                          <span className="font-medium text-foreground">{rule?.rRule}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('Date Range')}:</span>
                          <span className="font-medium text-foreground">
                            {rule?.startDateUtc ? appDate(parseApiDateOnly(rule.startDateUtc)) : '—'} - {rule?.endDateUtc && !rule.endDateUtc.startsWith('9999')
                              ? appDate(parseApiDateOnly(rule.endDateUtc))
                              : t('OpenEnded', { defaultValue: 'Open-ended' })}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('Time')}:</span>
                          <span className="font-medium text-foreground">
                            {startTime} - {endTime}
                          </span>
                        </div>
                        {hasBreakInfo && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">{t('Break')}:</span>
                            <span className="font-medium text-foreground">
                              {breakStart && breakEnd
                                ? `${breakStart} - ${breakEnd}${durationValue ? ` (${durationValue} min)` : ''}`
                                : (durationValue ? `${durationValue} min` : (breakStart || breakEnd || ''))}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ad-hoc Shifts */}
            {template?.embeddedShifts?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">{t('AdHocShifts')}</h3>
                <div className="space-y-3">
                  {template?.embeddedShifts?.map((shift) => {
                    // Convert minutes to time strings for display
                    const startTime = `${Math.floor(shift.startTimeMinutes / 60)}:${(shift.startTimeMinutes % 60).toString().padStart(2, '0')}`;
                    const endTime = `${Math.floor(shift.endTimeMinutes / 60)}:${(shift.endTimeMinutes % 60).toString().padStart(2, '0')}`;

                    return (
                      <div key={shift?.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium text-foreground">{shift?.date}</span>
                          <span className="text-sm text-muted-foreground">
                            {startTime} - {endTime}
                          </span>
                        </div>
                        {shift?.reason && (
                          <p className="text-sm text-muted-foreground italic">{shift?.reason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Assigned Users */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">{t('AssignedUsers')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {assignedUsers?.length > 0 ? (
                  assignedUsers?.map((user) => (
                    <div key={user?.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-medium">
                          {(user?.fullName || user?.name || 'U')?.split(' ')?.map(n => n?.[0])?.join('')}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">{user?.fullName || user?.name || t('UnknownUser')}</div>
                        <div className="text-xs text-muted-foreground">{user?.department || ''}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-sm text-muted-foreground italic">{t('NoUsersAssigned')}</div>
                )}
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Close')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
          >
            {t('DeleteTemplate')}
          </Button>
          <Button
            onClick={() => {
              onEditTemplate?.(template);
              onClose?.();
            }}
          >
            {t('EditTemplate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateDetailsModal;
